import { HomeAppointmentsTile, HomeAppointmentsTileSkeleton, useTelemedicineAccess } from "@internal/telemedicine"
import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useSelector } from "react-redux"
import { ConsumerCarrotPlanSidebar, PlanCardSkeleton, type ConsumerCareTeamMember } from "@internal/consumer/home-shell"
import { useCarrotCompanion } from "#/components/care-team/hooks/useCarrotCompanion"
import { formatCarrotCompanionName } from "#/components/care-team/utils/carrotCompanionNameUtil"
import { PageHeader } from "#/features/global-ui/page-header"
import { PageLayout } from "#/features/global-ui/page-layout"
import { getMemberFullName } from "#/redux/derivedSelectors"
import { getPreferredName } from "#/redux/reducers/userInfo"
import { Title } from "#/services/page-title/Title"
import { PiiText } from "#/services/tracking"
import { Paths } from "#/utils/Paths"
import { useBookingActionTracking } from "#/services/memberActionTracking/BookingTrackingProvider"
import { IntakeSubmittedDialog } from "#/pages/telemedicine/IntakeSubmittedDialog"

// The plan page is code-split and lazy-loaded (mirroring Root.tsx and the subscription billing
// page). The sidebar and the loading frame come from `./home-shell`, which is not code-split: a
// `Suspense` fallback cannot be a component from the chunk it is waiting on.
const ConsumerCarrotPlanPage = React.lazy(() =>
  import("@internal/consumer").then((m) => ({ default: m.ConsumerCarrotPlanPage }))
)

export function ConsumerHomePage(): JSX.Element {
  const { trackBookingEntry, trackPrepareForVisit } = useBookingActionTracking()
  const intl = useIntl()
  const memberFullName = useSelector(getMemberFullName)
  const preferredName = useSelector(getPreferredName)
  const displayName = preferredName ? preferredName : memberFullName

  const { data: telemedicineAccess, isLoading: telemedicineAccessLoading } = useTelemedicineAccess()
  // Eligible-but-unqualified members had their own "Learn more" link; the tile is now hidden for them
  // outright, so qualification is part of whether the tile shows at all rather than which link it shows.
  const showTelemedicine =
    telemedicineAccess != null &&
    telemedicineAccess.eligible &&
    telemedicineAccess.entry.status !== "single_unqualified"

  // The sidebar's "Your Care Team" tile, resolved here for the same reason the Appointments page
  // resolves its own companion in the host (`src/pages/telemedicine/appointments/page.tsx`):
  // `useCarrotCompanion` reads the monolithic `carrotClient` and the host's user context, neither of
  // which `@internal/consumer` can import. Null rather than a half-built object while the query is
  // in flight, so the package has one thing to check.
  // `isInitialLoading`, NOT `isLoading`: `useCarrotCompanion` is `enabled: !isInAppLockdown`, and a
  // disabled react-query v4 query with no cached data sits at `status: "loading"` forever, so
  // `isLoading` would leave a locked-down member looking at the care-team frame for good.
  const { data: carrotCompanion, isInitialLoading: carrotCompanionLoading } = useCarrotCompanion()
  const { displayName: companionName, initials } = formatCarrotCompanionName(carrotCompanion?.name)
  // Gated on the FORMATTED NAME, not on the record. A companion row can arrive with a null or
  // whitespace-only name, and both format to empty strings — so branching on `carrotCompanion`
  // handed the tile a truthy `{ displayName: "", initials: "" }` and it drew the blank avatar and
  // nameless role line that its own "no companion" branch exists to avoid.
  const careTeamMember: ConsumerCareTeamMember | null = companionName ? { displayName: companionName, initials } : null

  return (
    <PageLayout
      variant="sidebar"
      sidebar={
        <ConsumerCarrotPlanSidebar
          careTeamMember={careTeamMember}
          careTeamMemberLoading={carrotCompanionLoading}
          appointmentsTile={
            telemedicineAccessLoading ? (
              <HomeAppointmentsTileSkeleton />
            ) : (
              showTelemedicine && (
                <HomeAppointmentsTile
                  bookingPath={Paths.TELEMEDICINE_BOOKING}
                  appointmentsPath={Paths.TELEMEDICINE_APPOINTMENTS}
                  onBookAppointment={(event) => trackBookingEntry(event, "homepage_card")}
                  onPrepareForVisit={(appointmentId) => trackPrepareForVisit?.(appointmentId, "homepage_card")}
                />
              )
            )
          }
        />
      }
      header={
        <PageHeader
          pageTitle={
            <FormattedMessage
              defaultMessage="Hello, {displayName}"
              values={{ displayName: <PiiText>{displayName}</PiiText> }}
            />
          }
        />
      }
    >
      <Title title={intl.formatMessage({ defaultMessage: "Your Carrot Plan" })} />
      <IntakeSubmittedDialog />
      <React.Suspense fallback={<PlanCardSkeleton />}>
        <ConsumerCarrotPlanPage
          onBookAppointment={(event) => trackBookingEntry(event, "carrot_plan")}
          onPrepareForVisit={(appointmentId) => trackPrepareForVisit?.(appointmentId, "carrot_plan")}
        />
      </React.Suspense>
    </PageLayout>
  )
}
